import { AssetAggregate, DeviceProfile, Organisation, ViewWidth } from "@prisma/generated/client";
import { Match } from "effect";
import { VrmCode } from "./vrmTypes";

export enum UserRole {
  GLOBAL_ADMIN = "global_admin",
  ADMIN = "admin",
  MANAGER = "manager",
}

export type AppPermission =
  | "admin"
  | "organisation:manage"
  | "organisation:view"
  | "project:manage"
  | "project:view"
  | "zone:manage"
  | "zone:view"
  | "asset:view"
  | "user:manage"
  | "user:view"
  | "device:manage"
  | "device:view"
  | "profile:manage"
  | "profile:view"
  | "siteMap:manage"
  | "siteMap:view";

export const RolePermissions: Record<UserRole, AppPermission[]> = {
  [UserRole.GLOBAL_ADMIN]: [], // Has every permission
  [UserRole.ADMIN]: [
    "admin",
    "organisation:manage",
    "organisation:view",
    "user:manage",
    "user:view",
    "project:manage",
    "project:view",
    "zone:manage",
    "zone:view",
    "siteMap:manage",
    "siteMap:view",
    "device:manage",
    "device:view",
    // "asset:manage",
    // "asset:view",
  ],
  [UserRole.MANAGER]: [
    "project:manage",
    "project:view",
    "zone:manage",
    "zone:view",
    "siteMap:manage",
    "siteMap:view",
    // "asset:manage",
    // "asset:view",
    "user:view",
  ],
};

export type FlespiDeviceConfiguration = {
  ident: string;
  settings_polling: string;
  phone?: string;
};

export type FlespiDeviceMetadata = {
  category: keyof typeof DeviceCategory;
  fleetNumber: string;
  organisationId: string;
  vrmInstallationId?: string;
  allyeUnitId?: string;
  ownerId: string;
  profileId: string;
  make: string;
  model: string;
  mac: string;
  serial: string;
  switches?: SwitchMode[];
  calibration?: FuelSensorCalibration;
};

export type FuelSensorCalibration = {
  high?: number;
  low?: number;
};

export type FlespiDevice = {
  messages_ttl: number;
  protocol_id: number;
  protocol_name: string;
  device_type_id: keyof typeof DeviceTypes;
  device_type_name: string;
  id: number;
  enabled: boolean;
  connected: boolean;
  media_ttl: number;
  name: string;
  configuration: FlespiDeviceConfiguration;
  cid: number;
  media_rotate: number;
  messages_rotate: number;
  last_active: number;
  messages_size: number;
  metadata: FlespiDeviceMetadata;
};

export type FlespiDeviceLimited = {
  id: number;
  name: string;
  metadata: FlespiDeviceMetadata;
};

export type Metric = {
  key: string;
  label: string;
  uom: UnitOfMeasure;
};

export type UnitOfMeasure =
  | "V"
  | "A"
  | "kW"
  | "kWh"
  | "%"
  | "pF"
  | "Hz"
  | "VA"
  | "VAr"
  | "bool"
  | "C"
  | "Kpa"
  | "RPM"
  | "kVA"
  | "kVAr"
  | "'"
  | "hr"
  | "W"
  | "";

export type Field = {
  key: FieldTypes;
  unit: UnitOfMeasure;
  label: string;
  colour?: string;
};

export const UnitLabels: Record<UnitOfMeasure, string> = {
  V: "Voltage",
  A: "Current",
  kW: "Power",
  Hz: "Frequency",
  kWh: "Energy",
  "%": "Percentage",
  pF: "Power Factor",
  VA: "Volt Amps",
  VAr: "Reactive Volt Amps",
  bool: "Boolean",
  C: "Celsius",
  Kpa: "Kilopascal",
  RPM: "Revolutions Per Minute",
  kVA: "Apparent Power",
  kVAr: "Reactive Power",
  "'": "Angle",
  hr: "Hours",
  W: "Watts",
  "": "None",
};

export const UnitDecimals = {
  V: 1,
  A: 1,
  kW: 2,
  Hz: 1,
  pF: 2,
  kWh: 1,
  "%": 2,
  VA: 1,
  VAr: 1,
  bool: 0,
  C: 1,
  Kpa: 1,
  RPM: 0,
  kVA: 1,
  kVAr: 1,
  "'": 1,
  hr: 1,
  W: 1,
  "": 0,
};

export enum FieldTypes {
  V = "V",
  A = "A",
  kWh = "kWh",
  kW = "kW",
  pF = "pF",
  VAr = "VAr",
  VA = "VA",
  Hz = "Hz",
  cTHD = "cTHD",
  vTHD = "vTHD",
  MaxA = "MaxA",
  MaxkW = "MaxkW",
  TC = "TC",
  TP = "TP",
  L1N = "L1-N",
  L2N = "L2-N",
  L3N = "L3-N",
  L1L2 = "L1-L2",
  L2L3 = "L2-L3",
  L3L1 = "L3-L1",
  N = "N",
  SW = "SW",
  AVA = "AVA",
  Freq = "Freq",
  OP = "OP",
  CoTemp = "CoTemp",
  OilTemp = "OilTemp",
  IntFuel = "IntFuel",
  Batt = "Batt",
  EngSp = "EngSp",
  EngRT = "EngRT",
  kVA = "kVA",
  TkVA = "TkVA",
  kVAr = "kVAr",
  TkVAr = "TkVAr",
  VAng = "VAng",
  CAng = "CAng",
  Load = "L%",
  BV = "BV",
  BSOC = "BSOC",
  BA = "BA",
  BP = "BP",
  BTemp = "BTemp",
  InvS = "InvS",
  // CPH = "CPH",
  // VPH = "VPH"
}

/*
  AILIM: "Active input current limit",
  s: "Switch Position",
  bst: "Battery state",
  daily_kwh_genset: "Daily kWh from genset",
  daily_kwh_consumption: "Daily kWh consumption",
  */

export const fieldDefinitions: Field[] = [
  { key: FieldTypes.V, unit: "V", label: "Voltage" },
  { key: FieldTypes.A, unit: "A", label: "Current", colour: "var(--amps)" },
  { key: FieldTypes.kW, unit: "kW", label: "Power" },
  { key: FieldTypes.kWh, unit: "kWh", label: "Total Energy" },
  { key: FieldTypes.pF, unit: "pF", label: "Power Factor" },
  { key: FieldTypes.VA, unit: "VA", label: "Volt Amps" },
  { key: FieldTypes.VAr, unit: "VAr", label: "Reactive Volt Amps" },
  { key: FieldTypes.Hz, unit: "Hz", label: "Frequency" },
  { key: FieldTypes.cTHD, unit: "%", label: "Current THD" },
  { key: FieldTypes.vTHD, unit: "%", label: "Voltage THD" },
  { key: FieldTypes.MaxA, unit: "A", label: "Max Current" },
  { key: FieldTypes.MaxkW, unit: "kW", label: "Max Power" },
  { key: FieldTypes.TC, unit: "A", label: "Total Current" },
  { key: FieldTypes.TP, unit: "kW", label: "Total Power", colour: "var(--chart-energy)" },
  { key: FieldTypes.N, unit: "A", label: "Neutral" },
  { key: FieldTypes.L1N, unit: "V", label: "L1-N" },
  { key: FieldTypes.L2N, unit: "V", label: "L2-N" },
  { key: FieldTypes.L3N, unit: "V", label: "L3-N" },
  { key: FieldTypes.L1L2, unit: "V", label: "L1-L2" },
  { key: FieldTypes.L2L3, unit: "V", label: "L2-L3" },
  { key: FieldTypes.L3L1, unit: "V", label: "L3-L1" },
  { key: FieldTypes.SW, unit: "bool", label: "Switch" },
  { key: FieldTypes.AVA, unit: "bool", label: "Available" },
  { key: FieldTypes.OP, unit: "Kpa", label: "Oil Pressure" },
  { key: FieldTypes.Freq, unit: "Hz", label: "Alternator Frequency" },
  { key: FieldTypes.CoTemp, unit: "C", label: "Coolant Temp" },
  { key: FieldTypes.OilTemp, unit: "C", label: "Oil Temp" },
  { key: FieldTypes.IntFuel, unit: "%", label: "Internal Fuel Level" },
  { key: FieldTypes.Batt, unit: "V", label: "Battery Voltage" },
  { key: FieldTypes.EngSp, unit: "RPM", label: "Engine Speed" },
  { key: FieldTypes.EngRT, unit: "hr", label: "Engine Run Time" },
  { key: FieldTypes.kVA, unit: "kVA", label: "Apparent Power" },
  { key: FieldTypes.kVAr, unit: "kVAr", label: "Reactive Power" },
  { key: FieldTypes.TkVA, unit: "kVA", label: "Total Apparent Power" },
  { key: FieldTypes.TkVAr, unit: "kVAr", label: "Total Reactive Power" },
  { key: FieldTypes.VAng, unit: "'", label: "Voltage Phase Angle" },
  { key: FieldTypes.CAng, unit: "'", label: "Current Phase Angle" },
  { key: FieldTypes.Load, unit: "%", label: "Load" },
  { key: FieldTypes.BV, unit: "V", label: "Battery Voltage" },
  { key: FieldTypes.BSOC, unit: "%", label: "Battery State of Charge" },
  { key: FieldTypes.BA, unit: "A", label: "Battery Current" },
  { key: FieldTypes.BP, unit: "W", label: "Battery Power" },
  { key: FieldTypes.BTemp, unit: "C", label: "Battery Temp" },
  { key: FieldTypes.InvS, unit: "", label: "Inverter Status" },
];

export type FieldDirection = "in" | "out";

export type ProfileFieldMapping = {
  fields: {
    [modbusField: string]: UnitOfMeasure;
  }[];
  //[key: string]: string;
};

export const DeviceCategory = {
  "0": "Power Distribution",
  "1": "Generators",
  "2": "Fuel Monitor",
};

export const DeviceTypeValues = {
  NotLinked: "0",
  Galileosky7x: "803",
};

export const DeviceTypes = {
  [DeviceTypeValues.NotLinked]: "Not Linked (VRM, Allye)",
  [DeviceTypeValues.Galileosky7x]: "Galileosky 7x",
};

export const MeterTypes = {
  "1": "Eastron SDM230",
  "2": "Eastron SDM630",
  "3": "Rayleigh Instruments RI-F400",
  "4": "Rayleigh Instruments RI-F550",
  "5": "Cerbo GX",
  "6": "Eastron X96",
};

export const isSdmMeter = (type: string) => {
  return type === "1" || type === "2";
};

export type FormAction = "Create" | "Update";

export type FormStatusResult = { success: string } | { error: string } | { message: string };

export type AnalogKey = `ain.${number}`;
export type ModbusKey = `modbus.sensor.value.${number}`;
export type ModbusShortKey = `modbus_${number}`;

export type FlespiMessage = {
  ident: string;
  "device.id": number;
  "device.type.id": number;
  "device.name": string;
  profile_id: string;
  project_id: string;
  zone_id: string;
  asset_id: string;
  [key: AnalogKey]: number;
  [key: ModbusKey]: number;
  [key: ModbusShortKey]: number;
  timestamp: number;
  "gnss.antenna.status": boolean;
  "gsm.signal.dbm": number;
  "gsm.signal.level": number;
  "position.latitude": number;
  "position.longitude": number;
  "external.powersource.voltage": number;
};

export type VrmMessage = FlespiMessage & { [key in VrmCode]: any };
export type AllyeMessage = FlespiMessage & { [key in AllyeField]: any };

export type AllyeField =
  | "soc"
  | "ac_input_kw"
  | "ac_output_kw"
  | "status"
  | "inlet_status"
  | "inverter_status"
  | "total_kwh_in"
  | "total_kwh_out";

export type UserTokenData = {
  email: string;
  session_id: string;
  user_roles: string[];
};

export const getViewWidthLabel = (x: ViewWidth) =>
  Match.value(x).pipe(
    Match.when("Full", () => "Full Width"),
    Match.when("TwoThirds", () => "2/3"),
    Match.when("Half", () => "1/2"),
    Match.when("Third", () => "1/3"),
    Match.exhaustive,
  );

export type OrganisationWithChildren = Organisation & {
  children: (OrganisationWithChildren | null)[];
};

export enum SocketDisplayMode {
  Limited = "Limited",
  Full = "Full",
  Multi = "Multi",
}

export enum SwitchStatus {
  Off,
  Available,
  On,
}

export enum SwitchMode {
  Auto = 0,
  On = 1,
  Off = -1,
}

export interface MapData {
  assets: MapAsset[];
  connections: MapConnection[];
}

export interface MapAssetSettings {
  id: string;
  width: number;
  height: number;
  color: string;
}

export interface MapAsset extends MapAssetSettings {
  x: number;
  y: number;
  tooltipPosition: {
    left: number;
    top: number;
  };
}

export interface MapConnection {
  from: string;
  to: string;
}

// #region VRM Mapping

// #endregion

export type MonitorViewProps = {
  profile: DeviceProfile;
  messages: FlespiMessage[];
  aggregates: AssetAggregate[];
  latestMessage: FlespiMessage | undefined;
};

//#region Allye

export enum AllyeInverterStatus {
  Charging = 0,
  Discharging = 1,
  Off = 2,
}

export enum AllyeInletStatus {
  Off = 0,
  Running = 1,
  Fault = 2,
  RunningAnomalous = 3,
}

//#endregion

//export type DataAggregateWithMetrics = (DataAggregate & { metrics: DailyAggregateMetric[] });
